/**
 * Signal Engine - Deterministic rules for flagging anomalies
 * 
 * This module generates signals based on configurable thresholds.
 * All logic is deterministic - no AI involved.
 */

import type { Signal, SignalSeverity, MetricSeries, SignalType } from './types';
import type { MaterialityThresholds } from '@/lib/modules/types';

/**
 * Configuration for signal generation
 */
export interface SignalEngineConfig {
  /** Threshold for SPIKE_DROP (default: 0.30 = 30%) */
  spikeDropThreshold: number;
  /** Threshold for TREND_BREAK (default: 0.20 = 20%) */
  trendBreakThreshold: number;
  /** Materiality thresholds (optional) */
  thresholds?: MaterialityThresholds | null;
  /** Fiscal year end in YYYY-MM format (for NEAR_CLOSE) */
  fiscalYearEnd?: string;
}

/**
 * Default configuration
 */
export const defaultSignalConfig: SignalEngineConfig = {
  spikeDropThreshold: 0.30,
  trendBreakThreshold: 0.20,
};

let signalIdCounter = 0;

function generateSignalId(): string {
  return `signal_${++signalIdCounter}`;
}

/**
 * Generate all signals for given metrics
 */
export function generateSignals(
  metrics: MetricSeries[],
  config: SignalEngineConfig = defaultSignalConfig
): Signal[] {
  signalIdCounter = 0; // Reset counter for idempotent results
  const signals: Signal[] = [];
  
  for (const metric of metrics) {
    // 1. SPIKE_DROP - monthly change > threshold
    signals.push(...detectSpikeDrops(metric, config.spikeDropThreshold));
    
    // 2. TREND_BREAK - rolling 3-month average changes
    signals.push(...detectTrendBreaks(metric, config.trendBreakThreshold));
    
    // 3. THRESHOLD_HIT - exceeds materiality
    if (config.thresholds) {
      signals.push(...detectThresholdHits(metric, config.thresholds));
    }
    
    // 4. NEAR_CLOSE - last month in fiscal year
    if (config.fiscalYearEnd) {
      signals.push(...detectNearClose(metric, config.fiscalYearEnd));
    }
    
    // 5. DATA_QUALITY - missing or problematic data
    signals.push(...detectDataQualityIssues(metric));
  }
  
  return signals;
}

/**
 * Detect SPIKE_DROP signals - large changes vs previous period or year
 */
function detectSpikeDrops(metric: MetricSeries, threshold: number): Signal[] {
  const signals: Signal[] = [];
  
  for (let i = 1; i < metric.values.length; i++) {
    const prev = metric.values[i - 1];
    const curr = metric.values[i];
    const period = metric.periods[i];
    
    // Skip if previous is 0 (can't calculate percentage change)
    if (prev === 0 && curr === 0) continue;
    
    // Month-over-month change
    if (prev !== 0) {
      const momChange = (curr - prev) / Math.abs(prev);
      
      if (Math.abs(momChange) > threshold) {
        const severity = getSeverityFromChange(Math.abs(momChange));
        signals.push({
          id: generateSignalId(),
          signal_type: 'SPIKE_DROP',
          metric_key: metric.metric_key,
          period,
          severity,
          score: Math.min(1, Math.abs(momChange)),
          explanation: `${momChange > 0 ? 'Ökning' : 'Minskning'} med ${(momChange * 100).toFixed(0)}% mot föregående månad`,
          affected_accounts: metric.meta.accounts_included,
          change_value: momChange,
          is_yoy: false,
        });
      }
    }
    
    // Year-over-year change (if comparative data available)
    if (metric.comparative_values?.[i] && metric.comparative_values[i] !== 0) {
      const yoyChange = (curr - metric.comparative_values[i]) / Math.abs(metric.comparative_values[i]);
      
      if (Math.abs(yoyChange) > threshold) {
        const severity = getSeverityFromChange(Math.abs(yoyChange));
        signals.push({
          id: generateSignalId(),
          signal_type: 'SPIKE_DROP',
          metric_key: metric.metric_key,
          period,
          severity,
          score: Math.min(1, Math.abs(yoyChange)),
          explanation: `${yoyChange > 0 ? 'Ökning' : 'Minskning'} med ${(yoyChange * 100).toFixed(0)}% mot föregående år`,
          affected_accounts: metric.meta.accounts_included,
          change_value: yoyChange,
          is_yoy: true,
        });
      }
    }
  }
  
  return signals;
}

/**
 * Detect TREND_BREAK signals - rolling average changes direction
 */
function detectTrendBreaks(metric: MetricSeries, threshold: number): Signal[] {
  const signals: Signal[] = [];
  
  // Need at least 6 periods for meaningful trend analysis
  if (metric.values.length < 6) return signals;
  
  // Calculate rolling 3-month averages
  const rollingAvg: number[] = [];
  for (let i = 2; i < metric.values.length; i++) {
    const avg = (metric.values[i] + metric.values[i - 1] + metric.values[i - 2]) / 3;
    rollingAvg.push(avg);
  }
  
  // Look for significant changes in rolling average
  for (let i = 1; i < rollingAvg.length; i++) {
    const prev = rollingAvg[i - 1];
    const curr = rollingAvg[i];
    const period = metric.periods[i + 2]; // Offset by 2 due to rolling window
    
    if (prev === 0) continue;
    
    const change = (curr - prev) / Math.abs(prev);
    
    if (Math.abs(change) > threshold) {
      signals.push({
        id: generateSignalId(),
        signal_type: 'TREND_BREAK',
        metric_key: metric.metric_key,
        period,
        severity: Math.abs(change) > 0.40 ? 'HIGH' : 'MEDIUM',
        score: Math.min(1, Math.abs(change)),
        explanation: `Trendomslag: 3-månaders snitt ${change > 0 ? 'ökar' : 'minskar'} med ${(change * 100).toFixed(0)}%`,
        affected_accounts: metric.meta.accounts_included,
        change_value: change,
      });
    }
  }
  
  return signals;
}

/**
 * Detect THRESHOLD_HIT signals - values exceed materiality thresholds
 */
function detectThresholdHits(metric: MetricSeries, thresholds: MaterialityThresholds): Signal[] {
  const signals: Signal[] = [];
  
  const performanceMateriality = thresholds.performance_materiality?.value || 0;
  const overallMateriality = thresholds.overall_materiality?.value || 0;
  
  if (!performanceMateriality && !overallMateriality) return signals;
  
  // For each period, check if absolute change exceeds thresholds
  for (let i = 1; i < metric.values.length; i++) {
    const prev = metric.values[i - 1];
    const curr = metric.values[i];
    const period = metric.periods[i];
    const absoluteChange = Math.abs(curr - prev);
    
    if (overallMateriality && absoluteChange >= overallMateriality) {
      signals.push({
        id: generateSignalId(),
        signal_type: 'THRESHOLD_HIT',
        metric_key: metric.metric_key,
        period,
        severity: 'HIGH',
        score: Math.min(1, absoluteChange / overallMateriality),
        explanation: `Förändring ${formatAmount(absoluteChange)} överstiger väsentlighet (${formatAmount(overallMateriality)})`,
        affected_accounts: metric.meta.accounts_included,
        change_value: absoluteChange,
      });
    } else if (performanceMateriality && absoluteChange >= performanceMateriality) {
      signals.push({
        id: generateSignalId(),
        signal_type: 'THRESHOLD_HIT',
        metric_key: metric.metric_key,
        period,
        severity: 'MEDIUM',
        score: Math.min(1, absoluteChange / performanceMateriality),
        explanation: `Förändring ${formatAmount(absoluteChange)} överstiger prestanda (${formatAmount(performanceMateriality)})`,
        affected_accounts: metric.meta.accounts_included,
        change_value: absoluteChange,
      });
    }
  }
  
  return signals;
}

/**
 * Detect NEAR_CLOSE signals - last month of fiscal year
 */
function detectNearClose(metric: MetricSeries, fiscalYearEnd: string): Signal[] {
  const signals: Signal[] = [];
  
  // Find if fiscal year end is in our periods
  const endPeriod = metric.periods.find(p => p === fiscalYearEnd);
  if (!endPeriod) return signals;
  
  const endIndex = metric.periods.indexOf(endPeriod);
  const endValue = metric.values[endIndex];
  const prevValue = endIndex > 0 ? metric.values[endIndex - 1] : 0;
  
  // Flag if there's significant activity in closing month
  if (prevValue !== 0) {
    const change = (endValue - prevValue) / Math.abs(prevValue);
    
    if (Math.abs(change) > 0.15) { // Lower threshold for closing month
      signals.push({
        id: generateSignalId(),
        signal_type: 'NEAR_CLOSE',
        metric_key: metric.metric_key,
        period: endPeriod,
        severity: Math.abs(change) > 0.30 ? 'HIGH' : 'MEDIUM',
        score: Math.min(1, Math.abs(change)),
        explanation: `Bokslutsmånad med ${(change * 100).toFixed(0)}% förändring - granska periodiseringar`,
        affected_accounts: metric.meta.accounts_included,
        change_value: change,
      });
    }
  }
  
  return signals;
}

/**
 * Detect DATA_QUALITY signals - missing or problematic data
 */
function detectDataQualityIssues(metric: MetricSeries): Signal[] {
  const signals: Signal[] = [];
  
  // Check for gaps in data (all zeros in middle of series)
  let hasNonZero = false;
  let lastNonZeroIndex = -1;
  
  for (let i = 0; i < metric.values.length; i++) {
    if (metric.values[i] !== 0) {
      if (hasNonZero && i - lastNonZeroIndex > 2) {
        // Gap of more than 2 months with zeros
        signals.push({
          id: generateSignalId(),
          signal_type: 'DATA_QUALITY',
          metric_key: metric.metric_key,
          period: metric.periods[i - 1],
          severity: 'LOW',
          score: 0.5,
          explanation: `Möjlig datakvalitetsproblem: ${i - lastNonZeroIndex - 1} månader utan aktivitet`,
          affected_accounts: metric.meta.accounts_included,
        });
      }
      hasNonZero = true;
      lastNonZeroIndex = i;
    }
  }
  
  // Check if periods are non-contiguous (month gaps)
  for (let i = 1; i < metric.periods.length; i++) {
    const prev = metric.periods[i - 1];
    const curr = metric.periods[i];
    
    const [prevYear, prevMonth] = prev.split('-').map(Number);
    const [currYear, currMonth] = curr.split('-').map(Number);
    
    const expectedNextMonth = prevMonth === 12 ? 1 : prevMonth + 1;
    const expectedNextYear = prevMonth === 12 ? prevYear + 1 : prevYear;
    
    if (currYear !== expectedNextYear || currMonth !== expectedNextMonth) {
      signals.push({
        id: generateSignalId(),
        signal_type: 'DATA_QUALITY',
        metric_key: metric.metric_key,
        period: curr,
        severity: 'MEDIUM',
        score: 0.7,
        explanation: `Saknad period mellan ${prev} och ${curr}`,
        affected_accounts: metric.meta.accounts_included,
      });
    }
  }
  
  return signals;
}

/**
 * Helper: Get severity from percentage change
 */
function getSeverityFromChange(absChange: number): SignalSeverity {
  if (absChange >= 0.50) return 'HIGH';
  if (absChange >= 0.30) return 'MEDIUM';
  return 'LOW';
}

/**
 * Helper: Format amount for display
 */
function formatAmount(amount: number): string {
  if (Math.abs(amount) >= 1000000) {
    return `${(amount / 1000000).toFixed(1)} Mkr`;
  } else if (Math.abs(amount) >= 1000) {
    return `${(amount / 1000).toFixed(0)} tkr`;
  }
  return `${amount.toFixed(0)} kr`;
}

/**
 * Get signal counts by severity
 */
export function countSignalsBySeverity(signals: Signal[]): Record<SignalSeverity, number> {
  return {
    HIGH: signals.filter(s => s.severity === 'HIGH').length,
    MEDIUM: signals.filter(s => s.severity === 'MEDIUM').length,
    LOW: signals.filter(s => s.severity === 'LOW').length,
  };
}

/**
 * Filter signals for a specific metric
 */
export function getSignalsForMetric(signals: Signal[], metricKey: string): Signal[] {
  return signals.filter(s => s.metric_key === metricKey);
}

/**
 * Get unique periods that have signals
 */
export function getPeriodsWithSignals(signals: Signal[]): string[] {
  return [...new Set(signals.map(s => s.period))].sort();
}
